import { imageTypeSchema, type ImageType } from '@aikoa/common'

interface WebImageUploadPayload {
  fileData: ArrayBuffer
  fileName: string
  mimeType?: string
}

interface NormalizedWebImageUpload {
  fileData: ArrayBuffer
  fileName: string
  mimeType: ImageType
}

function normalizeMimeType(mimeType: string | undefined): string | undefined {
  const normalizedMimeType = mimeType?.split(';')[0]?.trim().toLowerCase()

  if (!normalizedMimeType) {
    return undefined
  }

  return normalizedMimeType
}

function replaceFileExtension(fileName: string, extension: string): string {
  const lastDotIndex = fileName.lastIndexOf('.')

  if (lastDotIndex === -1) {
    return `${fileName}.${extension}`
  }

  return `${fileName.slice(0, lastDotIndex)}.${extension}`
}

async function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(blob)

  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()

      image.onload = () => {
        resolve(image)
      }

      image.onerror = () => {
        reject(new Error('File type not supported'))
      }

      image.src = objectUrl
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function convertBlobToPng(blob: Blob): Promise<Blob> {
  const image = await loadImageFromBlob(blob)
  const canvas = document.createElement('canvas')
  const width = image.naturalWidth || image.width
  const height = image.naturalHeight || image.height

  if (!width || !height) {
    throw new Error('File type not supported')
  }

  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Processing failed')
  }

  context.drawImage(image, 0, 0, width, height)

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((normalizedBlob) => {
      if (!normalizedBlob) {
        reject(new Error('Processing failed'))
        return
      }

      resolve(normalizedBlob)
    }, 'image/png')
  })
}

export async function normalizeWebImageUpload({
  fileData,
  fileName,
  mimeType,
}: WebImageUploadPayload): Promise<NormalizedWebImageUpload> {
  const normalizedMimeType = normalizeMimeType(mimeType)
  const supportedMimeType = imageTypeSchema.safeParse(normalizedMimeType).data

  if (supportedMimeType) {
    return {
      fileData,
      fileName,
      mimeType: supportedMimeType,
    }
  }

  const sourceBlob = new Blob([fileData], {
    type: normalizedMimeType,
  })
  const normalizedBlob = await convertBlobToPng(sourceBlob)

  return {
    fileData: await normalizedBlob.arrayBuffer(),
    fileName: replaceFileExtension(fileName, 'png'),
    mimeType: 'image/png',
  }
}
